import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Network, Plus, X, Search, MapPin, Users, Banknote, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CollapsibleSection } from './CollapsibleSection';

const LABELS = {
  platform: { t: 'Platform', c: 'text-blue-300 bg-blue-500/10 border-blue-500/30' },
  addon: { t: 'Add-on', c: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' },
  competitor: { t: 'Competitor', c: 'text-red-300 bg-red-500/10 border-red-500/30' },
  other: { t: 'Related', c: 'text-slate-300 bg-slate-500/10 border-slate-500/30' },
};

const fmtRev = (v) => (v == null || v === '' ? null : `€${Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 })}m`);

function DealMeta({ deal }) {
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
      {deal.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{deal.location}</span>}
      {fmtRev(deal.revenue) && <span className="flex items-center gap-1"><Banknote className="w-3 h-3" />{fmtRev(deal.revenue)}</span>}
      {deal.employees && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{deal.employees}</span>}
      {deal.stage_label && <span className="text-muted-foreground/60">{deal.stage_label}</span>}
    </span>
  );
}

export function DealLinks({ projectId, isAcMember }) {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [label, setLabel] = useState('addon');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    api.getDealLinks(projectId).then((r) => setLinks(r.data.links || [])).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const onQuery = (v) => {
    setQuery(v);
    setSelected(null);
    clearTimeout(debounceRef.current);
    if (v.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(() => {
      api.searchLinkTargets(v.trim(), projectId).then((r) => setResults(r.data.results || [])).catch(() => {});
    }, 300);
  };

  const add = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.addDealLink(projectId, { target_id: selected.id, label, note });
      toast.success(`Linked to ${selected.code_name || selected.name}`);
      setAdding(false); setQuery(''); setResults([]); setSelected(null); setNote(''); setLabel('addon');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link deal');
    } finally { setBusy(false); }
  };

  const remove = async (link) => {
    try {
      await api.removeDealLink(link.link_id);
      setLinks((ls) => ls.filter((l) => l.link_id !== link.link_id));
      toast.success('Link removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove link');
    }
  };

  if (isAcMember && links.length === 0) return null;

  return (
    <CollapsibleSection
      storageKey={`deal.${projectId}.links`}
      icon={Network}
      iconClass="text-blue-400"
      title="Linked Deals"
      count={links.length || undefined}
      testId="deal-links-card"
      bodyClass="p-4 sm:p-5"
      right={!isAcMember && !adding ? (
        <Button onClick={(e) => { e.stopPropagation(); setAdding(true); }} size="sm" variant="outline"
          className="border-border rounded-sm text-xs h-8" data-testid="add-deal-link-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Link deal
        </Button>
      ) : null}
    >

      {adding && (
        <div className="border border-border rounded-sm p-3 mb-3 mt-2 space-y-2 bg-secondary/20" data-testid="deal-link-form">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search deals by company or code name…"
              className="bg-secondary border-border rounded-sm h-9 text-sm pl-8" data-testid="deal-link-search-input" autoFocus />
          </div>
          {!selected && results.length > 0 && (
            <div className="border border-border rounded-sm divide-y divide-border/60 max-h-56 overflow-y-auto">
              {results.map((r) => (
                <button key={r.id} onClick={() => { setSelected(r); setResults([]); setQuery(r.name || r.code_name); }}
                  className="w-full text-left px-3 py-2 hover:bg-secondary/50 transition-colors"
                  data-testid={`deal-link-result-${r.id}`}>
                  <span className="text-sm text-foreground">{r.name || r.code_name}</span>
                  {r.code_name && r.name && <span className="text-xs text-muted-foreground font-mono ml-2">{r.code_name}</span>}
                  <div className="mt-0.5"><DealMeta deal={r} /></div>
                </button>
              ))}
            </div>
          )}
          {!selected && query.trim().length >= 2 && results.length === 0 && (
            <p className="text-xs text-muted-foreground px-1">No matching deals found.</p>
          )}
          {selected && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={label} onValueChange={setLabel}>
                <SelectTrigger className="w-full sm:w-40 bg-secondary border-border rounded-sm h-9 text-sm" data-testid="deal-link-label-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border rounded-sm">
                  {Object.entries(LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`deal-link-label-${k}`}>{v.t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g. synergy idea)…"
                className="bg-secondary border-border rounded-sm h-9 text-sm flex-1" data-testid="deal-link-note-input" />
              <Button onClick={add} disabled={busy} size="sm"
                className="bg-blue-700 hover:bg-blue-600 text-white rounded-sm text-xs h-9 px-4" data-testid="deal-link-save-btn">
                Add link
              </Button>
            </div>
          )}
          <button onClick={() => { setAdding(false); setQuery(''); setResults([]); setSelected(null); }}
            className="text-[11px] text-muted-foreground hover:text-foreground" data-testid="deal-link-cancel-btn">Cancel</button>
        </div>
      )}

      {links.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-2">
          No linked deals yet — connect platform candidates, add-ons or competitors to map Buy &amp; Build potential.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-border/60">
          {links.map((l) => (
            <li key={l.link_id} className="py-2 flex items-start gap-3 group" data-testid={`deal-link-row-${l.link_id}`}>
              <span className={`shrink-0 mt-0.5 text-[10px] uppercase tracking-wide border rounded-sm px-1.5 py-0.5 font-mono ${(LABELS[l.label] || LABELS.other).c}`}>
                {(LABELS[l.label] || LABELS.other).t}
              </span>
              <button onClick={() => navigate(`/projects/${l.deal.id}`)} className="flex-1 min-w-0 text-left"
                data-testid={`deal-link-open-${l.deal.id}`}>
                <span className="text-sm text-foreground hover:text-blue-300 transition-colors inline-flex items-center gap-1.5">
                  {l.deal.name || l.deal.code_name}
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                </span>
                {l.deal.code_name && l.deal.name && (
                  <span className="text-[11px] text-muted-foreground font-mono ml-2">{l.deal.code_name}</span>
                )}
                <div className="mt-0.5"><DealMeta deal={l.deal} /></div>
                {l.note && <p className="text-[11px] text-muted-foreground/80 italic mt-0.5">"{l.note}"</p>}
              </button>
              {!isAcMember && (
                <button onClick={() => remove(l)} className="shrink-0 text-muted-foreground/40 hover:text-red-400 transition-colors mt-0.5"
                  data-testid={`deal-link-remove-${l.link_id}`} title="Remove link">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </CollapsibleSection>
  );
}
